import { detectedHighlights } from "../../../mocks/platformData";

const statusColors: Record<string, string> = {
  queued: "#F59E0B",
  processing: "#00D9FF",
  exported: "#10B981",
};

const statusLabels: Record<string, string> = {
  queued: "대기 중",
  processing: "처리 중",
  exported: "내보내기 완료",
};

const confidenceBadge = (score: number) => {
  if (score >= 90) return "#10B981";
  if (score >= 80) return "#F59E0B";
  return "#EF4444";
};

const detectionSignals = [
  { label: "발화 강도 급변", desc: "화자의 목소리 톤·속도·강도가 급격히 변화하는 순간 자동 감지", icon: "ri-mic-line" },
  { label: "감정 고조 구간", desc: "표정·발언 분석 기반으로 감정이 최고조에 달하는 장면 식별", icon: "ri-emotion-happy-line" },
  { label: "주제 희소성 탐지", desc: "화제성·희소성 높은 발언 구간을 콘텐츠 분류 모델로 자동 점수화", icon: "ri-fire-line" },
  { label: "침묵·긴장 패턴", desc: "발화 직전 침묵이나 장면 긴장감 상승 패턴을 밀리초 단위로 포착", icon: "ri-pause-circle-line" },
];

export default function HighlightDetectionSection() {
  return (
    <section className="py-28 bg-[#0F1419] relative overflow-hidden">
      <div className="absolute bottom-0 right-0 w-80 h-80 rounded-full bg-[#10B981]/5 blur-3xl pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <span className="text-[#10B981] text-xs font-semibold uppercase tracking-widest mb-4 block" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
            02 / 하이라이트 감지 엔진
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-5" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            핵심 순간, <span className="text-[#10B981]">즉시 감지</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
            ML 기반 감지 엔진이 방송 영상의 음성·표정·장면 패턴을 분석하여
            숏폼 성과 잠재력이 높은 구간을 자동으로 추출하고 순위를 매깁니다.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-3">
            <div className="bg-[#0A0E14] border border-white/8 rounded-2xl overflow-hidden">
              <div className="flex items-center justify-between px-6 py-4 border-b border-white/8">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 flex items-center justify-center">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#10B981] animate-pulse inline-block" />
                  </span>
                  <span className="text-white text-sm font-semibold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                    감지된 하이라이트
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[#00D9FF] text-xs font-mono">{detectedHighlights.length}개 세그먼트</span>
                  <button className="text-gray-500 hover:text-gray-300 cursor-pointer">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-filter-3-line text-sm" />
                    </span>
                  </button>
                </div>
              </div>

              <div className="divide-y divide-white/5">
                {detectedHighlights.map((h) => (
                  <div key={h.id} className="flex items-start gap-4 p-5 hover:bg-white/2 transition-colors group cursor-pointer">
                    <div className="relative shrink-0 w-28 h-16 rounded-lg overflow-hidden">
                      <img src={h.thumbnail} alt={h.title} className="w-full h-full object-cover object-top" />
                      <div className="absolute inset-0 bg-black/30" />
                      <div className="absolute bottom-1 right-1 bg-black/70 text-white text-xs px-1.5 py-0.5 rounded" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                        {h.duration}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <span className="text-white text-sm font-medium" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{h.title}</span>
                        <span
                          className="shrink-0 text-xs px-2 py-0.5 rounded-full border capitalize"
                          style={{ color: statusColors[h.status], borderColor: `${statusColors[h.status]}40`, backgroundColor: `${statusColors[h.status]}10` }}
                        >
                          {statusLabels[h.status]}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-gray-500" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                        <span className="flex items-center gap-1">
                          <span className="w-3 h-3 flex items-center justify-center"><i className="ri-time-line" /></span>
                          {h.timestamp}
                        </span>
                        <span className="flex items-center gap-1 text-[#F59E0B]">
                          <span className="w-3 h-3 flex items-center justify-center"><i className="ri-flashlight-line" /></span>
                          +{h.engagementSpike}% 급증
                        </span>
                      </div>
                      <div className="mt-3">
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="text-gray-600">AI 신뢰도</span>
                          <span style={{ color: confidenceBadge(h.confidence), fontFamily: "'JetBrains Mono', monospace" }}>{h.confidence}%</span>
                        </div>
                        <div className="h-1.5 bg-[#1A1F2E] rounded-full overflow-hidden">
                          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${h.confidence}%`, backgroundColor: confidenceBadge(h.confidence) }} />
                        </div>
                      </div>
                    </div>
                    <div className="hidden group-hover:flex items-center gap-2 shrink-0">
                      {["ri-edit-line", "ri-play-circle-line", "ri-upload-line"].map((icon) => (
                        <button key={icon} className="w-7 h-7 flex items-center justify-center bg-[#1A1F2E] hover:bg-[#00D9FF]/20 rounded-lg text-gray-400 hover:text-[#00D9FF] transition-colors cursor-pointer">
                          <i className={`${icon} text-sm`} />
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:col-span-2 space-y-6">
            <div className="bg-[#0A0E14] border border-white/8 rounded-2xl p-6">
              <h3 className="text-white font-semibold mb-5" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>감지 신호</h3>
              <div className="space-y-4">
                {detectionSignals.map((sig) => (
                  <div key={sig.label} className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-[#10B981]/10 rounded-lg shrink-0 mt-0.5">
                      <i className={`${sig.icon} text-[#10B981] text-sm`} />
                    </div>
                    <div>
                      <div className="text-white text-sm font-medium mb-0.5" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{sig.label}</div>
                      <div className="text-gray-500 text-xs leading-relaxed">{sig.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-[#0A0E14] border border-white/8 rounded-2xl p-6">
              <h3 className="text-white font-semibold mb-5" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>모델 성능</h3>
              {[
                { label: "정밀도", value: 94.2 },
                { label: "재현율", value: 91.7 },
                { label: "F1 점수", value: 92.9 },
              ].map((stat) => (
                <div key={stat.label} className="mb-4 last:mb-0">
                  <div className="flex justify-between text-xs mb-1.5">
                    <span className="text-gray-400">{stat.label}</span>
                    <span className="text-[#10B981]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{stat.value}%</span>
                  </div>
                  <div className="h-2 bg-[#1A1F2E] rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-[#10B981] to-[#00D9FF] rounded-full" style={{ width: `${stat.value}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
